/* Click-to-play video facade (WEB-6278; generalised + consent-gated in WEB-6470).
 *
 * Every third-party video surface in this theme renders a poster button and injects the iframe on
 * click, rather than server-rendering the <iframe>. Surfaces:
 *   - Professionals class rows      sections/sexyhair-professionals-classes.liquid  (original, WEB-6278)
 *   - PDP before/after video        sections/sexyhair-image-comparison.liquid
 *   - Care landing full video       sections/sexyhair-care-landing.liquid
 *   - Education article video       sections/sexyhair-main-article-education.liquid
 *   - Blog mid-body video block     snippets/sexyhair-article-sections.liquid
 * The last four were converted in WEB-6470; before that they server-rendered a live
 * `youtube.com/embed/<id>` src and leaked on 43 live production pages. The trigger markup is shared —
 * see snippets/sexyhair-video-embed.liquid.
 *
 * Why: a server-rendered YouTube iframe contacts Google on page load, before
 * the visitor has consented to anything. This repo already treats that as a
 * compliance problem, not a preference — Dawn's video.liquid and collage.liquid
 * were DELETED from themes/kenra/ in WEB-6442 for exactly this (see
 * docs/kenra-vppa-handling.md §3c), and Sexy Hair's OneTrust domain carries a
 * dedicated `C0007 Video Consent` group. sexyhair.com/professionals/ gates its
 * own videos the same way. The poster frames are self-hosted on Shopify Files,
 * so nothing on this page touches a third-party host until the visitor clicks.
 *
 * youtube-nocookie.com is used for the injected embed — same player, no
 * cookies set unless playback starts.
 *
 * Delegated listener: the rows are section-rendered, so this survives theme
 * editor re-renders without re-binding.
 */
(() => {
  const ORIGIN = 'https://www.youtube-nocookie.com/embed/';

  /* Bind once per page. The section carries a preset, so a merchant can add
   * "SH Pro Classes" twice and this file's <script src> renders twice — two
   * tags with the same src both execute. Without this guard two delegated
   * listeners bind, and the second one runs after the first has already
   * removed the trigger: `trigger.parentElement` is null and appendChild
   * throws, leaving the poster stranded and the video unplayable. Reproduced
   * by executing this file a second time on the page.
   * Same guard shape as assets/sexyhair-slider-dots.js. */
  if (window.__sexyhairVideoFacadeBound) return;
  window.__sexyhairVideoFacadeBound = true;

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-sh-video]');
    if (!trigger) return;

    const id = trigger.getAttribute('data-sh-video');
    // Video IDs are merchant settings. Restrict to YouTube's own alphabet so a
    // pasted URL or stray markup can never build a foreign iframe src.
    if (!id || !/^[A-Za-z0-9_-]{6,20}$/.test(id)) return;

    /* VPPA consent gate (WEB-6470) — FAIL CLOSED.
     *
     * Injecting the embed is the disclosure to YouTube, so it may only happen once OneTrust reports
     * Video Consent (C0007). `window.VPPAGate` is published by the VPPA app embed
     * (extensions/theme-extensions/assets/vppa-consent.js).
     *
     * When the gate IS present and consent is absent, we never get here: that file registers its
     * document click listener in the CAPTURE phase and calls stopImmediatePropagation(), so this
     * bubble-phase listener is not reached and it opens the OneTrust preference centre instead.
     *
     * When the gate is ABSENT — app embed not installed, disabled, or still loading — there is no
     * consent state to read, so we refuse rather than play. That asymmetry is the whole point:
     * WEB-6441 was a P0 precisely because a disabled app embed let video play un-consented for three
     * days. A dead click on a dev store without the embed is the correct trade.
     *
     * `event.preventDefault()` is deliberately AFTER this check — a refusal must not also swallow the
     * event, so anything else listening still sees a normal click. */
    const gate = window.VPPAGate;
    if (!gate || typeof gate.consented !== 'function' || gate.consented() !== true) return;

    event.preventDefault();

    const frame = document.createElement('iframe');
    frame.src = `${ORIGIN}${id}?rel=0&autoplay=1`;
    frame.title = trigger.getAttribute('data-sh-video-title') || 'Video';
    frame.className = 'sexyhair-video-facade__frame';
    frame.setAttribute('frameborder', '0');
    frame.setAttribute('allow', 'accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture');
    frame.setAttribute('allowfullscreen', '');

    /* Belt and braces alongside the bind-once guard above: if the trigger is
     * already detached, there is nowhere to put the player, so leave the DOM
     * alone rather than throw. */
    const host = trigger.parentElement;
    if (!host) return;

    trigger.remove();
    host.appendChild(frame);
    frame.focus();
  });
})();
